<script setup>
/* global axios */
// SMS gelen kutusunun cihaz paneli.
//
// Akis: uygulamayi indir -> QR okut -> telefon hesaba baglanir. QR'in
// icinde sunucu adresi ve tek kullanimlik bir kod var; okutulunca kalici
// jetona cevriliyor ve kod gecersizlesiyor.
//
// Her gelen kutusunun KENDI cihazi var: kiraci ikinci bir hat eklemek
// isterse ikinci bir SMS kutusu acar, o kutu kendi QR'ini gosterir.
//
// QR istemcide ureiliyor: kodu sunucudan alip burada goruntuye ceviriyoruz.
// Sunucuda gorsel uretip tasimanin bir faydasi yok.
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const route = useRoute();

// NEDEN route'tan: gelen kutusu serilestiricisi account_id alanini
// yaymiyor. props.inbox.account_id okumak adresi
// /api/v1/accounts/undefined/... yapiyor ve panel hicbir sey cizemiyordu.
const uc = computed(
  () => `/api/v1/accounts/${route.params.accountId}/sms-cihaz/${props.inbox.id}`
);

const cihaz = ref(null);
const webhookAdresi = ref('');
const apkAdresi = ref('');
const apkQr = ref('');
const qrGorsel = ref('');
// QR'in ICERIGI de duruyor: kod 10 dakikada bir yenileniyor ve sunucu yeni
// kod uretince ekrandaki gorsel de degismeli. Yalnizca "gorsel var mi" diye
// baksaydik kullanici suresi dolmus bir QR'i okutmaya calisirdi.
const qrIcerik = ref('');
const qrBitis = ref(null);
const yukleniyor = ref(false);
const islemde = ref(false);
const hata = ref('');

let sayac = null;

// Cihaz bagliyken durumu tazeleyip duruyoruz: kullanici telefonu acinca
// "cevrimici" yazisinin kendiliginden degismesi gerekiyor.
const TAZELEME = 10000;

const durumMetni = computed(() => {
  if (!cihaz.value) return '';
  if (!cihaz.value.eslesmis) return t('SMS_CIHAZ.DURUM.BAGLI_DEGIL');
  return cihaz.value.cevrimici
    ? t('SMS_CIHAZ.DURUM.CEVRIMICI')
    : t('SMS_CIHAZ.DURUM.CEVRIMDISI');
});

const durumRengi = computed(() => {
  if (!cihaz.value?.eslesmis) return 'text-n-slate-11 bg-n-alpha-1';
  return cihaz.value.cevrimici
    ? 'text-n-teal-11 bg-n-alpha-1'
    : 'text-n-amber-11 bg-n-alpha-1';
});

const qrCiz = async icerik => {
  qrIcerik.value = icerik;
  qrGorsel.value = await QRCode.toDataURL(icerik, { width: 320, margin: 1 });
};

const durumuYukle = async () => {
  try {
    const { data } = await axios.get(uc.value);
    cihaz.value = data.cihaz;
    webhookAdresi.value = data.webhook_url || '';
    hata.value = '';

    // Indirme QR'i bir kez uretiliyor; adres degismiyor.
    if (data.apk_url && !apkQr.value) {
      apkAdresi.value = data.apk_url;
      apkQr.value = await QRCode.toDataURL(data.apk_url, {
        width: 220,
        margin: 1,
      });
    }

    // Eslestirme QR'i sunucudan hazir geliyor; dugmeye basmadan gosteriyoruz.
    if (data.qr) {
      qrBitis.value = data.bitis;
      if (data.qr !== qrIcerik.value) await qrCiz(data.qr);
    } else {
      // Cihaz eslesti: QR'in ekranda kalmasinin anlami yok.
      qrGorsel.value = '';
      qrIcerik.value = '';
      qrBitis.value = null;
    }
  } catch (e) {
    hata.value = e?.response?.data?.error || t('SMS_CIHAZ.HATA');
  }
};

const qrUret = async () => {
  islemde.value = true;
  try {
    const { data } = await axios.post(`${uc.value}/eslestirme`);
    // QR icerigi sunucudan geliyor (sunucu adresi + tek kullanimlik kod);
    // burada yalnizca goruntuye ceviriyoruz.
    await qrCiz(data.qr);
    qrBitis.value = data.bitis;
    cihaz.value = data.cihaz;
  } catch (e) {
    useAlert(e?.response?.data?.error || t('SMS_CIHAZ.HATA'));
  } finally {
    islemde.value = false;
  }
};

const baglantiyiKaldir = async () => {
  // Geri alinamaz: telefonun jetonu gecersizlesiyor ve yeniden QR
  // okutmak gerekiyor. Once onay aliyoruz.
  if (!window.confirm(t('SMS_CIHAZ.KALDIR_ONAY'))) return;

  islemde.value = true;
  try {
    const { data } = await axios.delete(uc.value);
    cihaz.value = data.cihaz;
    qrGorsel.value = '';
    qrIcerik.value = '';
    useAlert(t('SMS_CIHAZ.KALDIRILDI'));
    // Eslesme koptu: yeni QR'i hemen getir, kullanici tekrar baglayabilsin.
    await durumuYukle();
  } catch (e) {
    useAlert(e?.response?.data?.error || t('SMS_CIHAZ.HATA'));
  } finally {
    islemde.value = false;
  }
};

const webhookKaydet = async () => {
  islemde.value = true;
  try {
    const { data } = await axios.post(`${uc.value}/webhook`, {
      webhook_url: webhookAdresi.value,
    });
    webhookAdresi.value = data.webhook_url || '';
    useAlert(t('SMS_CIHAZ.WEBHOOK_KAYDEDILDI'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('SMS_CIHAZ.HATA'));
  } finally {
    islemde.value = false;
  }
};

onMounted(async () => {
  yukleniyor.value = true;
  await durumuYukle();
  yukleniyor.value = false;
  sayac = setInterval(durumuYukle, TAZELEME);
});

onUnmounted(() => {
  if (sayac) clearInterval(sayac);
});
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <div v-if="yukleniyor" class="flex justify-center p-10">
      <Spinner class="text-n-brand" />
    </div>

    <template v-else>
      <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

      <!-- Durum -->
      <section
        class="flex flex-wrap items-center gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <span class="px-2 py-0.5 text-xs rounded-md" :class="durumRengi">
          {{ durumMetni }}
        </span>
        <span v-if="cihaz?.ad" class="text-sm text-n-slate-12">
          {{ cihaz.ad }}
        </span>
        <span v-if="cihaz?.telefon" class="text-xs text-n-slate-11">
          {{ cihaz.telefon }}
        </span>
        <span class="grow" />
        <span v-if="cihaz?.bekleyen" class="text-xs text-n-amber-11">
          {{ t('SMS_CIHAZ.BEKLEYEN', { sayi: cihaz.bekleyen }) }}
        </span>
      </section>

      <!-- Baglanmamis cihaz: iki adim yan yana. Kullanici telefonu elinde
           tutarken once uygulamayi kuruyor, sonra ayni ekrandaki ikinci
           QR'i okutuyor. -->
      <div v-if="!cihaz?.eslesmis" class="flex flex-wrap gap-6">
        <!-- 1. Adim: uygulamayi indir -->
        <section
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak grow basis-80"
        >
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('SMS_CIHAZ.INDIR_BASLIK') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('SMS_CIHAZ.INDIR_ACIKLAMA') }}
          </p>

          <img
            v-if="apkQr"
            :src="apkQr"
            alt=""
            class="p-2 bg-white rounded-lg"
            width="190"
            height="190"
          />
          <a
            :href="apkAdresi"
            class="text-xs break-all underline text-n-blue-11"
            target="_blank"
            rel="noopener"
          >
            {{ apkAdresi }}
          </a>
          <p class="text-xs text-n-amber-11">
            {{ t('SMS_CIHAZ.PLAY_PROTECT') }}
          </p>
        </section>

        <!-- 2. Adim: eslestirme -->
        <section
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak grow basis-80"
        >
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('SMS_CIHAZ.BASLIK') }}
          </h3>
          <p class="text-xs text-n-slate-11">{{ t('SMS_CIHAZ.ACIKLAMA') }}</p>

          <img
            v-if="qrGorsel"
            :src="qrGorsel"
            alt=""
            class="p-3 bg-white rounded-xl"
            width="230"
            height="230"
          />
          <Spinner v-else class="text-n-brand" />

          <p v-if="qrGorsel" class="text-xs text-n-slate-11">
            {{ t('SMS_CIHAZ.QR_SURE') }}
          </p>

          <div>
            <Button
              size="sm"
              variant="faded"
              icon="i-lucide-refresh-cw"
              :label="t('SMS_CIHAZ.QR_YENILE')"
              :is-loading="islemde"
              :disabled="islemde"
              @click="qrUret"
            />
          </div>
        </section>
      </div>

      <!-- Bagli cihaz -->
      <section v-else class="flex flex-col gap-3">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('SMS_CIHAZ.BASLIK') }}
        </h3>
        <div>
          <Button
            size="sm"
            variant="ghost"
            color="ruby"
            icon="i-lucide-unlink"
            :label="t('SMS_CIHAZ.KALDIR')"
            :disabled="islemde"
            @click="baglantiyiKaldir"
          />
        </div>
      </section>

      <!-- Webhook -->
      <section class="flex flex-col gap-2">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('SMS_CIHAZ.WEBHOOK_BASLIK') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('SMS_CIHAZ.WEBHOOK_ACIKLAMA') }}
        </p>
        <div class="flex flex-wrap items-center gap-2">
          <input
            v-model="webhookAdresi"
            type="url"
            class="!mb-0 grow min-w-[18rem]"
            placeholder="https://ornek.com/sms"
          />
          <Button
            size="sm"
            variant="faded"
            :label="t('SMS_CIHAZ.KAYDET')"
            :disabled="islemde"
            @click="webhookKaydet"
          />
        </div>
      </section>
    </template>
  </div>
</template>
